import { useQuery } from '@tanstack/react-query'
import { useDeferredValue, useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import type { PickOption } from './topologyApi'

/**
 * Выбор сотрудника или контрагента в формах топологии — по имени, через узкий эндпоинт.
 *
 * Не `RecordPicker`: тот ищет механизмом списков и требует права на сам справочник, а у
 * заведующего складом его нет — поиск отвечал «недоступно» именно тому, кто заполняет форму.
 * Сервер отдаёт здесь только id и имя (плюс должность или ИНН), поэтому выбор не открывает
 * карточек. Отказ поиска называется словами: пустой список читался бы как «таких нет».
 */
export function NamePicker({ id, kind, search, value, onChange }: {
  id: string
  /** Ключ запроса: у сотрудников и контрагентов кеши разные. */
  kind: string
  search: (query: string, signal?: AbortSignal) => Promise<PickOption[]>
  value: { id: string; label: string } | null
  onChange: (value: { id: string; label: string } | null) => void
}) {
  const { t } = useApp()
  const listId = useId()
  const [text, setText] = useState('')
  const query = useDeferredValue(text.trim())

  const found = useQuery({
    queryKey: ['topology-pick', kind, query],
    enabled: value === null && query.length >= 1,
    queryFn: ({ signal }) => search(query, signal),
  })

  if (value) {
    return (
      <span className="name-picker">
        <span>{value.label}</span>{' '}
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(null)} aria-label={t('picker.clear')}>
          ×
        </button>
      </span>
    )
  }

  const options = found.data ?? []
  const open = query.length >= 1 && options.length > 0
  return (
    <span className="name-picker stack" style={{ gap: 4 }}>
      <input id={id} className="input" role="combobox" aria-autocomplete="list" aria-expanded={open}
             aria-controls={listId} value={text} placeholder={t('topo.pick.search')}
             onChange={(event) => setText(event.target.value)} />
      {found.isError && <span className="t-xs muted" role="alert">{t('picker.failed')}</span>}
      {query.length >= 1 && found.isSuccess && options.length === 0 && (
        <span className="t-xs muted">{t('picker.nothing')}</span>
      )}
      {open && (
        <ul id={listId} role="listbox" aria-label={t('picker.results')} className="stack" style={{ gap: 2, listStyle: 'none', padding: 0, margin: 0 }}>
          {options.map((option) => (
            <li key={option.id}>
              <button type="button" role="option" aria-selected={false} className="btn btn-ghost btn-sm"
                      onClick={() => onChange({ id: option.id, label: option.extra ? `${option.name} · ${option.extra}` : option.name })}>
                {option.name}
                {option.extra && <span className="muted"> · {option.extra}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  )
}
